(() => {
  'use strict';

  const ROOT = '[data-rf-lumina-hero]';

  function init(root) {
    if (!root || root.dataset.rfLuminaInitialized === 'true') return;

    const viewport = root.querySelector('[data-rf-lumina-viewport]');
    const cards = Array.from(root.querySelectorAll('[data-rf-lumina-card]'));
    const currentLabel = root.querySelector('[data-rf-lumina-current]');
    const progress = root.querySelector('[data-rf-lumina-progress]');
    if (!viewport || !cards.length) return;

    root.dataset.rfLuminaInitialized = 'true';

    const autoplay = root.dataset.autoplay === 'true';
    const autoplayMs = Math.max(3000, Number(root.dataset.autoplayMs) || 5000);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const listeners = [];

    let activeIndex = 0;
    let timer = 0;
    let interactionTimer = 0;
    let pageScrollTimer = 0;
    let scrollRaf = 0;
    let isInView = !('IntersectionObserver' in window);
    let isPointerDown = false;
    let pointerStartX = 0;
    let pointerScrollLeft = 0;
    let observer = null;

    function listen(target, type, handler, options) {
      if (!target) return;
      target.addEventListener(type, handler, options);
      listeners.push(() => target.removeEventListener(type, handler, options));
    }

    const maxScroll = () => Math.max(1, viewport.scrollWidth - viewport.clientWidth);

    function nearestIndex() {
      const center = viewport.scrollLeft + viewport.clientWidth * 0.5;
      let nearest = 0;
      let distance = Infinity;

      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.offsetWidth * 0.5;
        const nextDistance = Math.abs(center - cardCenter);
        if (nextDistance < distance) {
          distance = nextDistance;
          nearest = index;
        }
      });

      return nearest;
    }

    function updateUI() {
      activeIndex = nearestIndex();
      cards.forEach((card, index) => card.classList.toggle('is-current', index === activeIndex));

      if (currentLabel) currentLabel.textContent = String(activeIndex + 1).padStart(2, '0');
      if (progress) {
        const ratio = cards.length <= 1 ? 1 : viewport.scrollLeft / maxScroll();
        progress.style.width = `${Math.max(0, Math.min(1, ratio)) * 100}%`;
      }
    }

    function scrollToIndex(index, smooth = true) {
      const next = (index + cards.length) % cards.length;
      const card = cards[next];
      if (!card) return;

      const paddingLeft = parseFloat(getComputedStyle(viewport).scrollPaddingLeft) || 0;
      viewport.scrollTo({
        left: Math.max(0, card.offsetLeft - paddingLeft),
        behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto'
      });
      activeIndex = next;
    }

    function stopTimer() {
      if (timer) window.clearTimeout(timer);
      timer = 0;
    }

    function canAutoplay() {
      return autoplay && cards.length > 1 && isInView && !document.hidden && !reduceMotion.matches && !isPointerDown;
    }

    function scheduleAutoplay(delay = autoplayMs) {
      stopTimer();
      if (!canAutoplay()) return;

      timer = window.setTimeout(() => {
        timer = 0;
        if (!canAutoplay()) return;
        const next = activeIndex >= cards.length - 1 ? 0 : activeIndex + 1;
        scrollToIndex(next, true);
        scheduleAutoplay();
      }, Math.max(500, delay));
    }

    function pauseThenResume(delay = Math.min(2400, autoplayMs)) {
      stopTimer();
      if (interactionTimer) window.clearTimeout(interactionTimer);
      interactionTimer = window.setTimeout(() => scheduleAutoplay(), delay);
    }

    function handleRailScroll() {
      if (scrollRaf) return;
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = 0;
        updateUI();
      });
    }

    function handlePointerDown(event) {
      pauseThenResume();
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest && event.target.closest('a')) return;

      isPointerDown = true;
      pointerStartX = event.clientX;
      pointerScrollLeft = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
      stopTimer();
      try { viewport.setPointerCapture(event.pointerId); } catch (_) {}
    }

    function handlePointerMove(event) {
      if (!isPointerDown) return;
      viewport.scrollLeft = pointerScrollLeft - (event.clientX - pointerStartX);
    }

    function endPointer() {
      if (!isPointerDown) return;
      isPointerDown = false;
      viewport.classList.remove('is-dragging');
      window.setTimeout(() => scrollToIndex(nearestIndex(), true), 40);
      pauseThenResume();
    }

    function handleKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        pauseThenResume();
        scrollToIndex(activeIndex + 1, true);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        pauseThenResume();
        scrollToIndex(activeIndex - 1, true);
      }
    }

    function handlePageScroll() {
      // Never let the horizontal autoplay animation compete with a user's vertical page scroll.
      if (!isInView || !autoplay) return;
      stopTimer();
      if (pageScrollTimer) window.clearTimeout(pageScrollTimer);
      pageScrollTimer = window.setTimeout(() => scheduleAutoplay(), 450);
    }

    function handleVisibility() {
      if (document.hidden) stopTimer();
      else scheduleAutoplay();
    }

    function handleMotionChange() {
      if (reduceMotion.matches) stopTimer();
      else scheduleAutoplay();
    }

    listen(viewport, 'scroll', handleRailScroll, { passive: true });
    listen(viewport, 'pointerdown', handlePointerDown);
    listen(viewport, 'pointermove', handlePointerMove);
    listen(viewport, 'pointerup', endPointer);
    listen(viewport, 'pointercancel', endPointer);
    listen(viewport, 'touchstart', () => {
      stopTimer();
      if (interactionTimer) window.clearTimeout(interactionTimer);
    }, { passive: true });
    listen(viewport, 'touchend', () => pauseThenResume(), { passive: true });
    listen(viewport, 'wheel', () => pauseThenResume(), { passive: true });
    listen(viewport, 'focusin', () => stopTimer());
    listen(viewport, 'focusout', () => pauseThenResume());
    listen(viewport, 'keydown', handleKeydown);
    listen(window, 'scroll', handlePageScroll, { passive: true });
    listen(document, 'visibilitychange', handleVisibility);

    if (typeof reduceMotion.addEventListener === 'function') {
      listen(reduceMotion, 'change', handleMotionChange);
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;

        isInView = entry.isIntersecting;
        if (isInView) {
          root.classList.add('is-in-view');
          scheduleAutoplay();
        } else {
          stopTimer();
        }
      }, { threshold: 0.12, rootMargin: '12% 0px 12% 0px' });
      observer.observe(root);
    } else {
      root.classList.add('is-in-view');
    }

    updateUI();
    scheduleAutoplay();

    root._rfLuminaCleanup = () => {
      stopTimer();
      if (interactionTimer) window.clearTimeout(interactionTimer);
      if (pageScrollTimer) window.clearTimeout(pageScrollTimer);
      if (scrollRaf) window.cancelAnimationFrame(scrollRaf);
      if (observer) observer.disconnect();
      listeners.splice(0).forEach((remove) => remove());
    };
  }

  function destroy(root) {
    if (!root) return;
    if (typeof root._rfLuminaCleanup === 'function') root._rfLuminaCleanup();
    root._rfLuminaCleanup = null;
    root.dataset.rfLuminaInitialized = '';
  }

  function initAll(scope = document) {
    if (scope.matches && scope.matches(ROOT)) init(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(ROOT).forEach(init);
  }

  const boot = () => initAll(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches && event.target.matches(ROOT)) destroy(event.target);
    if (event.target.querySelectorAll) event.target.querySelectorAll(ROOT).forEach(destroy);
  });
})();
